import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import hljs from '../utils/highlight';
import {
  CommentItem,
  createComment,
  deleteComment,
  getComments,
  updateComment,
  verifyCommentPassword,
} from '../services/CommentService';
import { addPostLike, downloadAttachment } from '../services/PostService';
import { getValidAccessTokenOrRefresh } from '../utils/AuthTokenUtil';

interface PostItemProps {
  post: {
    id: number;
    title: string;
    content: string;
    createdAt: string;
    thumbnailUrl?: string | null;
    category?: { name: string; parentName?: string | null };
    tags?: { id: number; name: string }[];
    attachments?: { id: number; originalFilename: string; contentType: string; size: number }[];
    commentCount: number;
    likeCount?: number;
  };
  onTagClick?: (tagName: string) => void;
  isAuthenticated?: boolean;
  currentUserNickname?: string;
  showUrlCopyButton?: boolean;
  onEdit?: (postId: number) => void;
  onDelete?: (post: PostItemProps['post']) => void;
}

type PasswordAction = 'edit' | 'delete';

const CODE_LANGUAGE_LABEL_PATTERN = /^Plaintext(?:COBOL)?(?:JavaScript|TypeScript|Markdown|Python|Ruby|Java|Perl|SQL|Bash|PHP)+$/;
const CODE_LANGUAGE_LABEL_ARTIFACT_PATTERN = /Plain\s*text\s*(?:COBOL\s*)?(?:(?:JavaScript|TypeScript|Markdown|Python|Ruby|Java|Perl|SQL|Bash|PHP)\s*){3,}/g;

const getThumbnailFromContent = (html: string) => {
  if (!html) {
    return '';
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const priorityImage = doc.querySelector<HTMLImageElement>('img[data-thumbnail-priority="true"], figure[data-thumbnail-priority="true"] img');
  const firstImage = doc.querySelector<HTMLImageElement>('img');
  return priorityImage?.getAttribute('src') || firstImage?.getAttribute('src') || '';
};

const getVideoEmbedUrl = (urlText: string) => {
  try {
    const url = new URL(urlText);
    const hostname = url.hostname.replace(/^www\./, '');

    if (hostname === 'youtu.be') {
      const videoId = url.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }

    if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
      const videoId = url.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }

      const paths = url.pathname.split('/').filter(Boolean);
      if ((paths[0] === 'embed' || paths[0] === 'shorts') && paths[1]) {
        return `https://www.youtube.com/embed/${paths[1]}`;
      }
    }

    if (hostname === 'tv.naver.com' || hostname === 'm.tv.naver.com') {
      const paths = url.pathname.split('/').filter(Boolean);
      const videoIndex = paths.indexOf('v');
      if (videoIndex >= 0 && paths[videoIndex + 1]) {
        return `https://tv.naver.com/embed/${paths[videoIndex + 1]}`;
      }

      const embedIndex = paths.indexOf('embed');
      if (embedIndex >= 0 && paths[embedIndex + 1]) {
        return `https://tv.naver.com/embed/${paths[embedIndex + 1]}`;
      }
    }

    if (hostname === 'serviceapi.nmv.naver.com') {
      return url.toString();
    }

    if (hostname === 'tv.kakao.com' || hostname === 'play-tv.kakao.com') {
      const paths = url.pathname.split('/').filter(Boolean);
      const cliplinkIndex = paths.indexOf('cliplink');
      if (cliplinkIndex >= 0 && paths[cliplinkIndex + 1]) {
        return `https://play-tv.kakao.com/embed/player/cliplink/${paths[cliplinkIndex + 1]}`;
      }

      const livelinkIndex = paths.indexOf('livelink');
      if (livelinkIndex >= 0 && paths[livelinkIndex + 1]) {
        return `https://play-tv.kakao.com/embed/player/livelink/${paths[livelinkIndex + 1]}`;
      }
    }
  } catch (error) {
    return '';
  }

  return '';
};

const renderVideoLinks = (contentElement: HTMLDivElement) => {
  contentElement.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    const href = link.getAttribute('href') || '';
    const embedUrl = getVideoEmbedUrl(href);

    if (!embedUrl) {
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.title = link.textContent || 'Embedded video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    link.replaceWith(iframe);
  });
};

const normalizeCodeLanguage = (language?: string | null) => {
  if (!language) {
    return '';
  }
  const normalized = language.trim().toLowerCase();
  return normalized === 'plaintext' ? 'plain' : normalized;
};

const getCodeLanguage = (element: Element) => {
  const dataLanguage = normalizeCodeLanguage(element.getAttribute('data-language'));
  const languageClass = Array.from(element.classList)
    .find((className) => className.startsWith('language-'))
    ?.replace('language-', '');

  return dataLanguage || normalizeCodeLanguage(languageClass) || 'plain';
};

const cleanCodeText = (text: string) => {
  const lines = text.replace(CODE_LANGUAGE_LABEL_ARTIFACT_PATTERN, '').replace(/\r\n?/g, '\n').split('\n');
  while (lines[0] && CODE_LANGUAGE_LABEL_PATTERN.test(lines[0].replace(/\s+/g, ''))) {
    lines.shift();
  }
  return lines.join('\n').replace(/^\n+/, '');
};

const isCodeLanguageLabelText = (text: string) => CODE_LANGUAGE_LABEL_PATTERN.test(text.replace(/\s+/g, ''));

const stripCodeLanguageLabelArtifacts = (text: string) => text.replace(CODE_LANGUAGE_LABEL_ARTIFACT_PATTERN, '');

const removeCodeLanguageLabelArtifacts = (contentElement: HTMLDivElement) => {
  contentElement.querySelectorAll('select, option, .code-block-language-control').forEach((control) => control.remove());
  contentElement.querySelectorAll<HTMLElement>('p, div, span').forEach((element) => {
    if (element.closest('pre, code, .ql-code-block-container, .ql-code-block')) {
      return;
    }
    const originalText = element.textContent || '';
    const strippedText = stripCodeLanguageLabelArtifacts(originalText);
    if (isCodeLanguageLabelText(originalText) || strippedText.trim().length === 0) {
      element.remove();
    } else if (strippedText !== originalText) {
      element.textContent = strippedText;
    }
  });

  const walker = document.createTreeWalker(contentElement, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node as Text);
    node = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    if (textNode.parentElement?.closest('pre, code, .ql-code-block-container, .ql-code-block')) {
      return;
    }
    const originalText = textNode.textContent || '';
    const strippedText = stripCodeLanguageLabelArtifacts(originalText);
    if (isCodeLanguageLabelText(originalText) || strippedText.trim().length === 0) {
      textNode.remove();
    } else if (strippedText !== originalText) {
      textNode.textContent = strippedText;
    }
  });
};

const getCodeText = (element: Element) => {
  const clone = element.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('select, option, .code-block-language-control').forEach((control) => control.remove());
  return cleanCodeText(clone.textContent || '');
};

const normalizeQuillCodeBlocks = (contentElement: HTMLDivElement) => {
  contentElement.querySelectorAll<HTMLElement>('.ql-code-block-container').forEach((container) => {
    const language = getCodeLanguage(container);
    const codeText = Array.from(container.querySelectorAll<HTMLElement>('.ql-code-block'))
      .map((line) => getCodeText(line))
      .join('\n') || getCodeText(container);
    const pre = document.createElement('pre');
    const code = document.createElement('code');

    pre.className = 'code-block-with-language';
    pre.setAttribute('data-language', language);
    code.className = `hljs language-${language === 'plain' ? 'plaintext' : language}`;
    code.textContent = cleanCodeText(codeText);

    pre.appendChild(code);
    container.replaceWith(pre);
  });

  contentElement.querySelectorAll<HTMLElement>('.ql-code-block').forEach((block) => {
    if (block.closest('pre')) {
      return;
    }

    const language = getCodeLanguage(block);
    const pre = document.createElement('pre');
    const code = document.createElement('code');

    pre.className = 'code-block-with-language';
    pre.setAttribute('data-language', language);
    code.className = `hljs language-${language === 'plain' ? 'plaintext' : language}`;
    code.textContent = getCodeText(block);

    pre.appendChild(code);
    block.replaceWith(pre);
  });
};

const PostItem: React.FC<PostItemProps> = ({ post, onTagClick, isAuthenticated = false, currentUserNickname, showUrlCopyButton = false, onEdit, onDelete }) => {
  const thumbnailUrl = post.thumbnailUrl || getThumbnailFromContent(post.content);
  const postContentHtml = useMemo(() => ({ __html: post.content }), [post.content]);
  const contentRef = useRef<HTMLDivElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  const likeEffectTimerRef = useRef<number | null>(null);
  const commentAuthorInputRef = useRef<HTMLInputElement>(null);
  const commentPasswordInputRef = useRef<HTMLInputElement>(null);
  const commentContentInputRef = useRef<HTMLTextAreaElement>(null);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);
  const [showLikeEffect, setShowLikeEffect] = useState(false);
  const [likeSubmitting, setLikeSubmitting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeComment, setActiveComment] = useState<CommentItem | null>(null);
  const [passwordAction, setPasswordAction] = useState<PasswordAction>('edit');
  const [author, setAuthor] = useState('');
  const [password, setPassword] = useState('');
  const [passwordDisplay, setPasswordDisplay] = useState('');
  const [content, setContent] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const data = await getComments(post.id);
      setComments(data);
      setCommentCount(data.length);
    } catch (error) {
      console.error('Failed to load comments', error);
    }
  }, [post.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    return () => {
      if (likeEffectTimerRef.current) {
        window.clearTimeout(likeEffectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) {
      return;
    }

    removeCodeLanguageLabelArtifacts(contentElement);
    normalizeQuillCodeBlocks(contentElement);
    removeCodeLanguageLabelArtifacts(contentElement);

    contentElement.querySelectorAll<HTMLPreElement>('pre[data-language]').forEach((pre) => {
      const language = pre.dataset.language || 'plain';
      const codeText = getCodeText(pre);
      const code = document.createElement('code');

      code.className = 'hljs';
      if (language !== 'plain' && hljs.getLanguage(language)) {
        code.classList.add(`language-${language}`);
        code.innerHTML = hljs.highlight(codeText, { language }).value;
      } else {
        code.classList.add('language-plaintext');
        code.textContent = codeText;
      }

      pre.replaceChildren(code);
    });

    renderVideoLinks(contentElement);
  }, [post.content]);

  const resetForm = () => {
    setAuthor('');
    setPassword('');
    setPasswordDisplay('');
    setContent('');
    setVerifiedPassword('');
    setError('');
    setSubmitting(false);
    if (commentAuthorInputRef.current) {
      commentAuthorInputRef.current.value = '';
    }
    if (commentPasswordInputRef.current) {
      commentPasswordInputRef.current.value = '';
    }
  };

  const clearGuestCommentCredentials = () => {
    if (isAuthenticated) {
      return;
    }
    setAuthor('');
    setPassword('');
    if (commentAuthorInputRef.current) {
      commentAuthorInputRef.current.value = '';
    }
    if (commentPasswordInputRef.current) {
      commentPasswordInputRef.current.value = '';
    }
  };

  const handleAuthorChange = (value: string) => {
    if (value.trim().toLowerCase().startsWith('jdbc:')) {
      setAuthor('');
      if (commentAuthorInputRef.current) {
        commentAuthorInputRef.current.value = '';
      }
      return;
    }
    setAuthor(value);
  };

  const scrollToComments = () => {
    commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCommentLinkClick = () => {
    scrollToComments();
  };

  const getPostUrl = () => `${window.location.origin}/posts/${post.id}`;

  const copyText = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const handleCopyPostUrl = async () => {
    try {
      await copyText(getPostUrl());
      setUrlCopied(true);
      window.setTimeout(() => setUrlCopied(false), 1400);
    } catch (error) {
      console.error('Failed to copy post URL', error);
      alert('URL 복사에 실패했습니다.');
    }
  };
  const openCreateModal = () => {
    resetForm();
    if (isAuthenticated) {
      setAuthor(currentUserNickname || '로그인 사용자');
      setPasswordDisplay(`temp-${Math.random().toString(36).slice(2, 6)}`);
    }
    setShowCreateModal(true);
    if (!isAuthenticated) {
      [0, 100, 500].forEach((delay) => {
        window.setTimeout(clearGuestCommentCredentials, delay);
      });
    }
  };

  const handleCreateModalEntered = () => {
    if (isAuthenticated) {
      commentContentInputRef.current?.focus();
      return;
    }
    commentAuthorInputRef.current?.focus();
  };

  const handleLikeClick = async () => {
    if (likeSubmitting) {
      return;
    }

    setLikeSubmitting(true);

    try {
      const result = await addPostLike(post.id);
      setLikeCount(result.post.likeCount || 0);

      if (result.liked) {
        if (likeEffectTimerRef.current) {
          window.clearTimeout(likeEffectTimerRef.current);
        }

        setShowLikeEffect(true);
        likeEffectTimerRef.current = window.setTimeout(() => {
          setShowLikeEffect(false);
          likeEffectTimerRef.current = null;
        }, 900);
      }
    } catch (error) {
      console.error('Failed to add post like', error);
    } finally {
      setLikeSubmitting(false);
    }
  };

  const handleAttachmentDownload = async (attachment: { id: number; originalFilename: string }) => {
    try {
      const token = isAuthenticated ? await getValidAccessTokenOrRefresh() : null;
      await downloadAttachment(post.id, attachment.id, attachment.originalFilename, token);
    } catch (error) {
      console.error('Failed to download attachment', error);
      alert('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const handleCreateComment = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = isAuthenticated ? await getValidAccessTokenOrRefresh() : null;
      if (isAuthenticated && !token) {
        setError('로그인 상태가 만료되었습니다. 다시 로그인해주세요.');
        return;
      }

      if (!isAuthenticated && author.trim().toLowerCase().startsWith('jdbc:')) {
        clearGuestCommentCredentials();
        setError('사용자명을 다시 입력해주세요.');
        return;
      }

      await createComment(post.id, {
        author: author.trim(),
        password: isAuthenticated ? passwordDisplay : password,
        content: content.trim(),
      }, token);
      await loadComments();
      resetForm();
      setShowCreateModal(false);
      setTimeout(scrollToComments, 0);
    } catch (error: any) {
      setError(error.response?.data?.message || '댓글 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const openPasswordModal = (comment: CommentItem, action: PasswordAction) => {
    if (isAuthenticated) {
      if (action === 'edit') {
        setActiveComment(comment);
        setContent(comment.content);
        setVerifiedPassword('');
        setError('');
        setShowEditModal(true);
        return;
      }

      handleAuthenticatedDelete(comment);
      return;
    }

    setActiveComment(comment);
    setPasswordAction(action);
    setPassword('');
    setError('');
    setShowPasswordModal(true);
  };

  const handleAuthenticatedDelete = async (comment: CommentItem) => {
    if (submitting) {
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const token = await getValidAccessTokenOrRefresh();
      if (!token) {
        setError('로그인 상태가 만료되었습니다. 다시 로그인해주세요.');
        return;
      }
      await deleteComment(comment.id, '', token);
      await loadComments();
    } catch (error: any) {
      setError(error.response?.data?.message || '댓글 삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeComment) {
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      if (passwordAction === 'edit') {
        const verifiedComment = await verifyCommentPassword(activeComment.id, password);
        setActiveComment(verifiedComment);
        setContent(verifiedComment.content);
        setVerifiedPassword(password);
        setPassword('');
        setShowPasswordModal(false);
        setShowEditModal(true);
      } else {
        await deleteComment(activeComment.id, password);
        await loadComments();
        setShowPasswordModal(false);
        setActiveComment(null);
        setPassword('');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || '비밀번호 확인에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeComment) {
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const token = isAuthenticated ? await getValidAccessTokenOrRefresh() : null;
      if (isAuthenticated && !token) {
        setError('로그인 상태가 만료되었습니다. 다시 로그인해주세요.');
        return;
      }

      await updateComment(activeComment.id, {
        password: isAuthenticated ? '' : verifiedPassword,
        content: content.trim(),
      }, token);
      await loadComments();
      resetForm();
      setShowEditModal(false);
      setActiveComment(null);
    } catch (error: any) {
      setError(error.response?.data?.message || '댓글 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className={`mb-5 border-0 border-bottom rounded-0 pb-4 post-item ${thumbnailUrl ? 'has-thumbnail' : ''}`}>
      <Card.Body className="p-0">
        <h2 className="post-title mb-3">
          <Link to={`/posts/${post.id}`}>{post.title}</Link>
        </h2>
        <div className="post-meta text-muted mb-4 small">
          <div className="post-meta-info">
            <Badge bg="secondary" className="me-2">
              {post.category ? [post.category.parentName, post.category.name].filter(Boolean).join(' > ') : 'Uncategorized'}
            </Badge>
            <span className="me-3">{new Date(post.createdAt).toLocaleString()}</span>
            <button type="button" className="comment-link" onClick={handleCommentLinkClick}>
              댓글 {commentCount}
            </button>
            {showUrlCopyButton && (
              <button
                type="button"
                className="post-url-button"
                onClick={handleCopyPostUrl}
                aria-label="게시글 URL 복사"
                title={getPostUrl()}
              >
                <i className="bi bi-link-45deg" aria-hidden="true" />
                <span>{urlCopied ? '복사됨' : 'URL'}</span>
              </button>
            )}
          </div>
          {isAuthenticated && (
            <div className="post-admin-actions">
              <button type="button" aria-label="게시글 수정" onClick={() => onEdit?.(post.id)}>
                <i className="bi bi-pencil-square" />
              </button>
              <button type="button" aria-label="게시글 삭제" onClick={() => onDelete?.(post)}>
                <i className="bi bi-trash3" />
              </button>
            </div>
          )}
        </div>
        {thumbnailUrl && (
          <Link to={`/posts/${post.id}`} className="post-thumbnail-link" aria-label={`${post.title} 상세 보기`}>
            <img src={thumbnailUrl} alt="" aria-hidden="true" loading="lazy" />
          </Link>
        )}
        <div
          ref={contentRef}
          className="post-content"
          dangerouslySetInnerHTML={postContentHtml}
        />
        {post.tags && post.tags.length > 0 && (
          <div className="post-tags">
            {post.tags.map((tag) => (
              <button key={tag.id} type="button" className="post-tag" onClick={() => onTagClick?.(tag.name)}>
                #{tag.name}
              </button>
            ))}
          </div>
        )}

        {post.attachments && post.attachments.length > 0 && (
          <div className="post-attachments">
            {post.attachments.map((attachment) => (
              <button
                key={attachment.id}
                type="button"
                className="post-attachment"
                onClick={() => handleAttachmentDownload(attachment)}
              >
                <i className="bi bi-paperclip" />
                <span>{attachment.originalFilename}</span>
              </button>
            ))}
          </div>
        )}

        <section ref={commentsRef} className="comment-section">
          <div className="comment-head">
            <div className="like-action">
              <button type="button" className="like-button" onClick={handleLikeClick} disabled={likeSubmitting} aria-label="좋아요">
                <i className="bi bi-heart-fill" />
                <span>{likeCount}</span>
              </button>
              {showLikeEffect && <span className="like-effect">추천 +1</span>}
            </div>
            <Button variant="outline-dark" size="sm" onClick={openCreateModal}>댓글 달기</Button>
          </div>

          {comments.length > 0 ? (
            <div className="comment-list">
              {comments.map((comment) => (
                <article key={comment.id} className="comment-item">
                  <div className="comment-meta">
                    <strong>{comment.author}</strong>
                    <span>{new Date(comment.createdAt).toLocaleString()}</span>
                  </div>
                  <p>{comment.content}</p>
                  <div className="comment-actions">
                    <button type="button" aria-label="댓글 수정" onClick={() => openPasswordModal(comment, 'edit')}>
                      <i className="bi bi-pencil-square" />
                    </button>
                    <button type="button" aria-label="댓글 삭제" onClick={() => openPasswordModal(comment, 'delete')}>
                      <i className="bi bi-trash3" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="comment-empty">등록된 댓글이 없습니다.</div>
          )}
        </section>
      </Card.Body>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} onEntered={handleCreateModalEntered} centered>
        <Form onSubmit={handleCreateComment} autoComplete="off">
          <Modal.Header closeButton>
            <Modal.Title>댓글 달기</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="comment-author-row">
              <Form.Group>
                <Form.Label>사용자</Form.Label>
                <Form.Control
                  ref={commentAuthorInputRef}
                  name={`borine-comment-author-${post.id}`}
                  value={author}
                  onChange={(event) => handleAuthorChange(event.target.value)}
                  onFocus={(event) => handleAuthorChange(event.currentTarget.value)}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  disabled={isAuthenticated}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>비밀번호</Form.Label>
                <Form.Control
                  ref={commentPasswordInputRef}
                  name={`borine-comment-password-${post.id}`}
                  type="password"
                  value={isAuthenticated ? passwordDisplay : password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  disabled={isAuthenticated}
                  required
                />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>댓글내용</Form.Label>
              <Form.Control
                ref={commentContentInputRef}
                as="textarea"
                rows={5}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                required
              />
            </Form.Group>
            {error && <p className="text-danger mt-3 mb-0">{error}</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)}>취소</Button>
            <Button variant="dark" type="submit" disabled={submitting}>{submitting ? '저장 중...' : '저장'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <Form onSubmit={handlePasswordSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>비밀번호 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>비밀번호</Form.Label>
              <Form.Control type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
            </Form.Group>
            {error && <p className="text-danger mt-3 mb-0">{error}</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowPasswordModal(false)}>취소</Button>
            <Button variant={passwordAction === 'delete' ? 'danger' : 'dark'} type="submit" disabled={submitting}>
              {passwordAction === 'delete' ? '삭제' : '확인'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Form onSubmit={handleUpdateComment}>
          <Modal.Header closeButton>
            <Modal.Title>댓글 수정</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>댓글내용</Form.Label>
              <Form.Control as="textarea" rows={5} value={content} onChange={(event) => setContent(event.target.value)} required />
            </Form.Group>
            {error && <p className="text-danger mt-3 mb-0">{error}</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>취소</Button>
            <Button variant="dark" type="submit" disabled={submitting}>{submitting ? '저장 중...' : '저장'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Card>
  );
};

export default PostItem;

